import { Editor } from "@tiptap/react";
import * as Menus from "@tiptap/react/menus";
import { useEditorState } from "@tiptap/react";
import { useState } from "react";
import {
  Box,
  Button,
  Icon,
  Input,
  VStack,
  HStack,
  Text,
  Divider,
} from "@chakra-ui/react";
import {
  FaBold,
  FaItalic,
  FaLink,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaCheck,
  FaTimes,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";
import { EditorButton } from "./EditorButton";
import { ColorSelector } from "./ColorSelector";

// @ts-ignore
const { BubbleMenu } = Menus;

// Configuration interface for bubble menu
export interface BubbleMenuConfig {
  showBold?: boolean;
  showItalic?: boolean;
  showLink?: boolean;
  showColorSelector?: boolean;
  showTextAlign?: boolean;
  placement?: "top" | "bottom";
  offset?: number;
}

// Default bubble menu configuration
export const defaultBubbleMenuConfig: BubbleMenuConfig = {
  showBold: true,
  showItalic: true,
  showLink: true,
  showColorSelector: true,
  showTextAlign: false,
  placement: "bottom",
  offset: 8,
};

interface BubbleMenuComponentProps {
  editor: Editor;
  config?: BubbleMenuConfig;
}

export function BubbleMenuComponent({
  editor,
  config = defaultBubbleMenuConfig,
}: BubbleMenuComponentProps) {
  const [showMenu, setShowMenu] = useState(true);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [showExtendedMenu, setShowExtendedMenu] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTitle, setLinkTitle] = useState("");

  // Use editor state to make the component reactive to selection changes
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isBold: ctx.editor.isActive("bold"),
        isItalic: ctx.editor.isActive("italic"),
        isStrike: ctx.editor.isActive("strike"),
        isLink: ctx.editor.isActive("link"),
        isTextAlignLeft: ctx.editor.isActive({ textAlign: "left" }),
        isTextAlignCenter: ctx.editor.isActive({ textAlign: "center" }),
        isTextAlignRight: ctx.editor.isActive({ textAlign: "right" }),
      };
    },
  });

  if (!editor || !showMenu) {
    return null;
  }

  const handleLinkButtonClick = () => {
    if (editor.isActive("link")) {
      // If link is active, show current URL and current text content in inputs
      const currentUrl = editor.getAttributes("link").href || "";

      // Get the full link text by extending to the full mark range
      editor.commands.extendMarkRange("link");
      const fullLinkText = editor.state.doc.textBetween(
        editor.state.selection.from,
        editor.state.selection.to
      );

      setLinkUrl(currentUrl);
      setLinkTitle(fullLinkText);
      setShowLinkInput(true);
    } else {
      // If no link is active, check if there's selected text
      const hasSelection = editor.state.selection.content().size > 0;
      let selectedText = "";

      if (hasSelection) {
        selectedText = editor.state.doc.textBetween(
          editor.state.selection.from,
          editor.state.selection.to
        );
      }

      setLinkUrl("");
      setLinkTitle(selectedText);
      setShowLinkInput(true);
    }
  };

  const handleLinkSubmit = () => {
    if (linkUrl.trim() === "") {
      // Remove link if URL is empty
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      // Set or update link with URL
      try {
        const url = linkUrl.trim();

        console.log("Setting link with URL:", url);

        if (editor.isActive("link")) {
          // Update existing link - only URL, keep existing text
          editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: url })
            .run();
        } else {
          // Create new link
          const hasSelection = editor.state.selection.content().size > 0;

          if (hasSelection) {
            // If text is selected, turn it into a link
            editor.chain().focus().setLink({ href: url }).run();
          } else {
            // If no text is selected, insert new content as link
            editor
              .chain()
              .focus()
              .insertContent(linkTitle.trim() || "Link")
              .setLink({ href: url })
              .run();
          }
        }

        console.log("After update:", editor.getAttributes("link"));
      } catch (e) {
        console.error("Error setting link:", e);
      }
    }
    setShowLinkInput(false);
    setLinkUrl("");
    setLinkTitle("");
  };

  const handleLinkCancel = () => {
    setShowLinkInput(false);
    setLinkUrl("");
    setLinkTitle("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLinkSubmit();
    } else if (e.key === "Escape") {
      handleLinkCancel();
    }
  };

  return (
    <BubbleMenu
      editor={editor}
      options={{
        placement: config.placement || "bottom",
        offset: config.offset || 8,
      }}
    >
      <Box
        className="bubble-menu"
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        boxShadow="lg"
        p={1}
        display="flex"
        gap={1}
        zIndex={1000}
        alignItems="center"
      >
        {!showLinkInput ? (
          <>
            {config.showBold && (
              <EditorButton
                isActive={editorState.isBold}
                onClick={() => editor.chain().focus().toggleBold().run()}
              >
                <Icon as={FaBold} />
              </EditorButton>
            )}

            {config.showItalic && (
              <EditorButton
                isActive={editorState.isItalic}
                onClick={() => editor.chain().focus().toggleItalic().run()}
              >
                <Icon as={FaItalic} />
              </EditorButton>
            )}

            {config.showLink && (
              <EditorButton
                isActive={editorState.isLink}
                onClick={handleLinkButtonClick}
              >
                <Icon as={FaLink} />
              </EditorButton>
            )}

            {config.showColorSelector ? (
              <ColorSelector editor={editor} />
            ) : null}

            {showExtendedMenu && (
              <>
                <Divider h={6} orientation="vertical" borderColor="gray.300" />
                <EditorButton
                  isActive={editorState.isTextAlignLeft}
                  onClick={() =>
                    editor.chain().focus().setTextAlign("left").run()
                  }
                >
                  <Icon as={FaAlignLeft} />
                </EditorButton>
                <EditorButton
                  isActive={editorState.isTextAlignCenter}
                  onClick={() =>
                    editor.chain().focus().setTextAlign("center").run()
                  }
                >
                  <Icon as={FaAlignCenter} />
                </EditorButton>
                <EditorButton
                  isActive={editorState.isTextAlignRight}
                  onClick={() =>
                    editor.chain().focus().setTextAlign("right").run()
                  }
                >
                  <Icon as={FaAlignRight} />
                </EditorButton>
              </>
            )}

            <Divider h={6} orientation="vertical" borderColor="gray.300" />
            <EditorButton
              // isActive={showExtendedMenu}
              onClick={() => setShowExtendedMenu(!showExtendedMenu)}
            >
              <Icon as={showExtendedMenu ? FaChevronLeft : FaChevronRight} />
            </EditorButton>
          </>
        ) : (
          <Box minW="350px" p={3}>
            <VStack spacing={3} align="stretch">
              <Box>
                <Text fontWeight="bold" mb={1} fontSize="sm">
                  URL
                </Text>
                <Input
                  size="sm"
                  placeholder="Enter URL"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoFocus
                />
              </Box>

              <Box>
                <Text fontWeight="bold" mb={1} fontSize="sm">
                  Title
                </Text>
                <Input
                  size="sm"
                  placeholder="Enter title"
                  value={linkTitle}
                  onChange={(e) => setLinkTitle(e.target.value)}
                  onKeyDown={handleKeyDown}
                  isDisabled={editor.isActive("link")}
                  bg={editor.isActive("link") ? "gray.100" : "white"}
                />
              </Box>

              <HStack spacing={2} justify="flex-end">
                <Button size="sm" variant="outline" onClick={handleLinkCancel}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  bg="black"
                  color="white"
                  onClick={handleLinkSubmit}
                >
                  Save
                </Button>
              </HStack>
            </VStack>
          </Box>
        )}
      </Box>
    </BubbleMenu>
  );
}
